import { ArrowLeftRight, X } from 'lucide-preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { text } from '#contract'
import { useClient } from '../app/client-context.ts'
import { useView } from '../app/use-view.ts'
import { useT } from '../i18n/index.ts'
import { BinSlot } from './bin-slot.tsx'
import { CursorStack } from './cursor-stack.tsx'
import { InventoryGrid } from './inventory-grid.tsx'
import styles from './inventory-screen.module.css'
import { InventoryHints } from './inventory-hints.tsx'
import { ItemTooltip } from './item-tooltip.tsx'
import { ScreenUiContext, createScreenUi, useScreenUi } from './screen-context.ts'
import { useGridFocus } from './use-grid-focus.ts'

/**
 * The inventory screen, over the running game: the opened storage on the left and the
 * player's slots on the right (stacked when there's no room, the player's below), the bin
 * under the player's, the held stack on the cursor, a tooltip, and the controls for the
 * current device. Every click goes to the engine, which owns the rules and the held stack.
 */
export function InventoryScreen() {
  const t = useT()
  const client = useClient()
  const root = useRef<HTMLDivElement>(null)
  const [ui] = useState(createScreenUi)
  const hasOther = useView((view) => (view.inventory?.screen?.other ?? null) !== null)
  const touch = useView((view) => view.input.device === 'touch')
  const gamepad = useView((view) => view.input.device === 'gamepad')
  useGridFocus(root)

  // The held stack follows the pointer everywhere, not only over slots.
  useEffect(() => {
    const controller = new AbortController()
    window.addEventListener(
      'pointermove',
      (event) => {
        ui.movePointer({ x: event.clientX, y: event.clientY })
      },
      { signal: controller.signal },
    )
    return () => {
      controller.abort()
    }
  }, [ui])

  // A gamepad starts on the first slot.
  useEffect(() => {
    if (gamepad) root.current?.querySelector<HTMLElement>('[data-target]')?.focus()
  }, [gamepad, hasOther])

  return (
    <ScreenUiContext.Provider value={ui}>
      <div
        ref={root}
        class={styles.screen}
        onContextMenu={(event) => {
          event.preventDefault()
        }}
      >
        <button
          type="button"
          class={styles.close}
          aria-label={t(text('inventory.close'))}
          tabIndex={-1}
          onClick={() => {
            client?.inventory.close()
          }}
        >
          <X aria-hidden="true" />
        </button>
        <div class={styles.panels}>
          {hasOther && <InventoryGrid side="other" />}
          <div class={styles.own}>
            <InventoryGrid
              side="player"
              actions={touch && hasOther ? <QuickToggle /> : undefined}
            />
            <BinSlot />
          </div>
        </div>
        <InventoryHints />
        <ItemTooltip />
        <CursorStack />
      </div>
    </ScreenUiContext.Provider>
  )
}

/** Touch's Shift: while on, taps move stacks across instead of taking them. */
function QuickToggle() {
  const t = useT()
  const ui = useScreenUi()
  return (
    <button
      type="button"
      class={styles.quick}
      aria-pressed={ui.quick.value}
      onClick={() => {
        ui.toggleQuick()
      }}
    >
      <ArrowLeftRight aria-hidden="true" />
      {t(text('inventory.quickMove'))}
    </button>
  )
}
